import { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const checkUser = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setAuthError(null);
      } else {
        // If response is not ok, clear the user but don't block the UI
        console.warn(`Auth check failed with status: ${response.status}`);
        setUser(null);
        setAuthError(`Authentication failed: ${response.status}`);
      }
    } catch (error) {
      // Handle network errors or timeouts gracefully
      if (error.name === 'AbortError') {
        console.warn('Auth check timed out');
        setAuthError('Authentication check timed out');
      } else {
        console.error('Error checking auth status:', error);
        setAuthError(`Authentication error: ${error.message}`);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  const value = {
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    refreshAuth: checkUser, // Add this function to manually refresh auth state
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 